// File: src/components/Reveal/ChatInput.jsx
import React, { useEffect, useRef, useState } from "react";
import { LuSend } from "react-icons/lu";

export default function ChatInput({
  onSend,
  placeholder = "Send message…",
  disabled = false,
  autoFocus = false,
  style = {},
  maxHeight = 120,
}) {
  const [value, setValue] = useState("");
  const [sending, setSending] = useState(false);            // ⬅️ in-flight guard
  const taRef = useRef(null);
  const lastSentAtRef = useRef(0);                          // ⬅️ burst throttle

  // Autosize helpers
  function measureBase(el) {
    const cs = window.getComputedStyle(el);
    const lh = parseFloat(cs.lineHeight);
    const line = Number.isFinite(lh) ? lh : parseFloat(cs.fontSize) || 14;
    const pad =
      (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
    const border =
      (parseFloat(cs.borderTopWidth) || 0) + (parseFloat(cs.borderBottomWidth) || 0);
    return Math.ceil(line + pad + border);
  }
  function autosize(el, base) {
    if (!el || !base) return;
    el.style.height = "auto";
    const need = el.scrollHeight;
    const next = Math.min(Math.max(need, base), maxHeight);
    el.style.height = next + "px";
    el.style.overflowY = next >= maxHeight ? "auto" : "hidden";
  }

  useEffect(() => {
    if (!taRef.current) return;
    const base = measureBase(taRef.current);
    taRef.current.style.minHeight = base + "px";
    autosize(taRef.current, base);
  }, []);

  useEffect(() => {
    if (!taRef.current) return;
    const base = parseFloat(taRef.current.style.minHeight) || 0;
    autosize(taRef.current, base);
  }, [value, maxHeight]);

  async function handleSend() {
    const text = value.trim();
    if (!text || disabled || sending) return;

    // simple burst throttle (blocks very fast double taps/presses)
    const now = Date.now();
    if (now - lastSentAtRef.current < 450) return;
    lastSentAtRef.current = now;

    setSending(true);

    // optimistic clear to prevent duplicate re-sends from Enter spam
    setValue("");
    try {
      await onSend(text);
    } catch (e) {
      // restore text so the user can retry
      setValue(text);
      console.warn("[ChatInput] send failed:", e?.response?.data || e?.message);
    } finally {
      setSending(false);
    }
  }

  const canSend = !disabled && !sending && !!value.trim();

  return (
    <div
      style={{
        // matches NavigateSpotPage composer bar
        position: "relative",
        padding: "10px 12px calc(10px + env(safe-area-inset-bottom, 0px))",
        background: "#fff",
        borderTop: "0px solid #eef0f2",
        ...style,
      }}
    >
   <div style={{ position: "relative", display: "flex", gap: 8 }}>
  <textarea
    ref={taRef}
    value={value}
    onChange={(e) => setValue(e.target.value)}
    placeholder={placeholder}
    disabled={disabled || sending}
    autoFocus={autoFocus}
    rows={1}
    onKeyDown={(e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        if (!sending) handleSend(); // ⬅️ ignore while in-flight
      }
    }}
    onBlur={() => {
      // ✅ Nudge visualViewport listeners on iOS to restore layout after keyboard closes
      requestAnimationFrame(() => window.dispatchEvent(new Event("resize")));
    }}
    style={{
      flex: 1,
      borderRadius: 10,
      border: "1px solid #E7E9EE",
      padding: "10px 40px 10px 12px",
      fontSize: 14,
      outline: "none",
      background: disabled || sending ? "#f9fafb" : "#fff",
      resize: "none",
      lineHeight: 1.4,
      boxSizing: "border-box",
      maxHeight,
      overflowY: "hidden",
      fontFamily: "'Open Sans', sans-serif",
      opacity: disabled || sending ? 0.7 : 1,
    }}
  />

        {/* Send icon button */}
        <button
          type="button"
          aria-label={sending ? "Sending…" : "Send message"}
          onClick={handleSend}
          disabled={!canSend}
          style={{
            position: "absolute",
            top: "50%",
            right: 8,
            transform: "translateY(-50%)",
            width: 28,
            height: 28,
            borderRadius: 999,
            border: "none",
            background: "transparent",
            display: "grid",
            placeItems: "center",
            padding: 0,
            cursor: canSend ? "pointer" : "default",
            opacity: canSend ? 1 : 0.5,
          }}
        >
          <LuSend size={18} color={canSend ? "#007BFF" : "#A0AEC0"} />
        </button>
      </div>
    </div>
  );
}
